import { FunctionComponent } from 'react'
import DOMPurify from 'dompurify'
import OneOrMoreQuestionPage from './OneOrMoreQuestionPage'
import WeekRadioButtons from './WeekDaysQuestion'
import { Question } from 'Frontend/scripts/models/foodHabits.model'
import { useTranslation } from 'react-i18next'

type Props = {
  question: Question
  answers: { [key: string]: number | number[] }
  handleAnswer: (questionId: string, value: number | number[]) => void
}

const QuestionComponent: FunctionComponent<Props> = ({ question, answers, handleAnswer }) => {
  const { t } = useTranslation()
  const sanitizedQuestionText = question.questionText ? DOMPurify.sanitize(question.questionText) : ''

  return (
    <div className="question__container">
      {question.heading && <h1 className="question__heading foodhabits-h1">{question.heading}</h1>}
      {question.questionText && (
        <h2 className="question__heading" dangerouslySetInnerHTML={{ __html: sanitizedQuestionText }}></h2>
      )}
      {question.informationText && question.type === 'InformationPage' && (
        <p className="question__info--big">{question.informationText}</p>
      )}
      {question.image && question.image.imageUrl && (
        <div className="question__image-container">
          <img
            src={question.image.imageUrl}
            alt={question.image.alternativeText}
            className="question__image img-fluid"
          />
        </div>
      )}
      <div className="question__slider-container">
        {question.type === 'SliderQuestionPage' && question.sliderOptions && (
          <>
            {question.sliderOptions[answers[question.id] as number]?.imageUrl && (
              <img
                src={question.sliderOptions[answers[question.id] as number].imageUrl}
                alt="Selected Option"
                className="question__slider-image"
              />
            )}
          </>
        )}
        {question.type === 'SliderQuestionPage' && question.sliderOptions && (
          <>
            <input
              type="range"
              min={0}
              max={question.sliderOptions.length - 1}
              step={1}
              value={(answers[question.id] as number) ?? 0}
              onChange={(e) => handleAnswer(question.id, Number(e.target.value))}
              className="question__slider form-range"
            />
          </>
        )}
      </div>
      {question.type === 'NumberOfDaysaWeekQuestionPage' && question.inputType === 'radio' && (
        <WeekRadioButtons question={question} answers={answers} handleAnswer={handleAnswer} />
      )}
      {question.type === 'OneOrMoreQuestionPage' && question.answers && question.inputType === 'radio' && (
        <>
          <OneOrMoreQuestionPage
            options={question.answers}
            contentId={question.id}
            multiChoice={question.multiChoice}
            answers={answers}
            onChange={handleAnswer}
          />
        </>
      )}
      {question.type === 'YesNoQuestionPage' && (
        <div className="question__yes-no mt-5">
          <button
            className={`question__button yes-no-btn me-2 ${answers[question.id] === 50 ? 'selected' : ''}`}
            onClick={() => handleAnswer(question.id, 50)}
          >
            {t('yes')}
          </button>
          <button
            className={`question__button yes-no-btn ${answers[question.id] === 5 ? 'selected' : ''}`}
            onClick={() => handleAnswer(question.id, 5)}
          >
            {t('no')}
          </button>
        </div>
      )}
      <InfoComponent question={question} />
    </div>
  )
}

const InfoComponent: FunctionComponent<{ question: Question }> = ({
  question: { informationImageUrl, informationText, type },
}) => {
  if (!informationImageUrl && !informationText) return null

  return (
    <p className="question__info">
      {informationImageUrl && <img className="m-2" src={informationImageUrl} />}
      {informationText && type !== 'InformationPage' && informationText}
    </p>
  )
}

export default QuestionComponent
